import React, { FC, useEffect, useState, useRef } from 'react'
import { Button, Form, Spinner, Badge, Row, Col } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import useAppToast, { getApiErrorMsg } from 'src/hooks/useAppToast'
import {
  createOmsShipmentFulfilledAsync,
  updateOmsShipmentFulfilledAsync,
  updateOmsShipmentStatusAsync,
  getOmsShipmentListAsync,
} from 'src/redux/actions/omsShippingActions'
import ShippingCarrierSelect from './ShippingCarrierSelect'

// ===================================================================
// Types
// ===================================================================

/** All supported modal modes. Extend this union to add new modes. */
export type ShipmentModalMode = 'create' | 'edit' | 'deliver' | 'view'

export type ShipmentFulfillmentModalProps = {
  onCancel: () => void
  orderId: number | null
  orderCode?: string
  pkg: any | null
  orderLinesList: any[]
  onSuccess: () => void
  isDirect?: boolean // Flow 2: direct shipment without package
  /** Explicit mode override. When provided, bypasses auto-derivation from pkg.shipment.shipment_status. */
  mode?: ShipmentModalMode
  /** Pre-loaded shipment data for edit/deliver/view when called from ManageShipmentsModal. */
  shipmentData?: any | null
  packageIndex?: number
  shipmentIndex?: number
}

// ===================================================================
// Mode Configuration Map
// ===================================================================

type ModalModeConfig = {
  title: string
  fieldsDisabled: boolean
  deliveryDateVisible: boolean
  deliveryDateDisabled: boolean
  submitButtonText: string
  submitButtonVariant: string
  submitButtonIcon: string
  submittingText: string
  showSubmitButton: boolean
  closeButtonText: string
}

/**
 * Single source of truth for all mode-specific UI configuration.
 * Adding a new mode? Add an entry here — TypeScript will enforce exhaustiveness.
 */
const MODAL_CONFIG: Record<ShipmentModalMode, ModalModeConfig> = {
  create: {
    title: '🚚 Create Shipment',
    fieldsDisabled: false,
    deliveryDateVisible: false,
    deliveryDateDisabled: false,
    submitButtonText: '🚚 Mark as Shipped',
    submitButtonVariant: 'primary',
    submitButtonIcon: '🚚',
    submittingText: 'Creating...',
    showSubmitButton: true,
    closeButtonText: 'Cancel',
  },
  edit: {
    title: '✏️ Edit Shipment',
    fieldsDisabled: false,
    deliveryDateVisible: false,
    deliveryDateDisabled: false,
    submitButtonText: '💾 Update Mark as Shipped',
    submitButtonVariant: 'primary',
    submitButtonIcon: '💾',
    submittingText: 'Updating...',
    showSubmitButton: true,
    closeButtonText: 'Cancel',
  },
  deliver: {
    title: '📦 Mark as Delivered',
    fieldsDisabled: true,
    deliveryDateVisible: true,
    deliveryDateDisabled: false,
    submitButtonText: '✅ Mark as Delivered',
    submitButtonVariant: 'success',
    submitButtonIcon: '✅',
    submittingText: 'Marking...',
    showSubmitButton: true,
    closeButtonText: 'Cancel',
  },
  view: {
    title: '👁️ View Shipment',
    fieldsDisabled: true,
    deliveryDateVisible: true,
    deliveryDateDisabled: true,
    submitButtonText: '',
    submitButtonVariant: '',
    submitButtonIcon: '',
    submittingText: '',
    showSubmitButton: false,
    closeButtonText: 'Close',
  },
}

/** Exhaustive mode check — compile-time error if a mode is unhandled. */
function assertNever(x: never): never {
  throw new Error(`Unhandled modal mode: ${x}`)
}

// ===================================================================
// Helpers
// ===================================================================

// Format "2026-03-25T11:24" → "2026-03-25 11:24:00"
const formatDateTimeForApi = (val: string): string => {
  if (!val) return ''
  return val.replace('T', ' ') + ':00'
}

// Format "2026-03-25 11:24:00" → "2026-03-25T11:24" for datetime-local input
const toInputDateTime = (val: string): string => {
  if (!val) return ''
  return val.replace(' ', 'T').slice(0, 16)
}

// Get current date-time as "YYYY-MM-DDTHH:mm" for datetime-local input
const getCurrentDateTimeLocal = (): string => {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

// ===================================================================
// Component
// ===================================================================

const ShipmentFulfillmentForm: FC<ShipmentFulfillmentModalProps> = ({
  onCancel,
  orderId,
  orderCode,
  pkg,
  orderLinesList,
  onSuccess,
  isDirect = false,
  mode: explicitMode,
  shipmentData,
  packageIndex,
  shipmentIndex,
}) => {
  const dispatch = useAppDispatch()
  const { successToast, errorToast } = useAppToast()

  const { saving: shipmentSaving, detailLoading: shipmentDetailLoading } = useAppSelector(
    (s) => s.omsShipping
  )

  // ── Mode resolution ──
  // If explicit mode is passed (from ManageShipmentsModal), use it directly.
  // Otherwise, auto-derive from pkg.shipment.shipment_status (backward compat for package flow).
  const resolveMode = (): ShipmentModalMode => {
    if (explicitMode) return explicitMode
    const status = pkg?.shipment?.shipment_status ?? null
    if (status === 'SHIPPED') return 'deliver'
    if (status === 'DELIVERED') return 'view'
    return 'create'
  }

  const activeMode = resolveMode()
  const config = MODAL_CONFIG[activeMode]

  // ── Shipment form fields ──
  const [shipmentNumber, setShipmentNumber] = useState('')
  const [shipmentMethod, setShipmentMethod] = useState('')
  const [customShipmentMethod, setCustomShipmentMethod] = useState('')
  const [shipDate, setShipDate] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [trackingUrl, setTrackingUrl] = useState('')

  // The shipment ID (fetched from GET API or passed via shipmentData)
  const [fetchedShipmentId, setFetchedShipmentId] = useState<number | null>(null)

  // ── Editable order line items (for edit mode) ──
  const [editableOrderItems, setEditableOrderItems] = useState<Record<number, number>>({})

  // ── State reset ──
  const resetFormState = () => {
    setShipmentNumber('')
    setShipmentMethod('')
    setCustomShipmentMethod('')
    setShipDate(getCurrentDateTimeLocal())
    setDeliveryDate('')
    setTrackingNumber('')
    setTrackingUrl('')
    setFetchedShipmentId(null)
    setEditableOrderItems({})
  }

  // ── Hydrate direct create when order lines load ──
  const hydratedCreateRef = useRef(false)
  useEffect(() => {
    if (activeMode === 'create' && isDirect && orderLinesList.length > 0 && !hydratedCreateRef.current) {
      hydrateDirectCreate()
      hydratedCreateRef.current = true
    }
  }, [activeMode, isDirect, orderLinesList]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mount logic ──
  useEffect(() => {
    // For package flow, pkg is required; for direct flow, only orderId is needed
    if (!isDirect && !pkg && !shipmentData) return

    // If shipmentData is passed directly (from ManageShipmentsModal), hydrate from it
    if (shipmentData && (activeMode === 'edit' || activeMode === 'deliver' || activeMode === 'view')) {
      hydrateFromShipmentData(shipmentData)
      return
    }

    // Backward compat: auto-derive behavior from activeMode
    switch (activeMode) {
      case 'create':
        resetFormState()
        // Hydration for direct create is handled by the dedicated useEffect watching orderLinesList
        break
      case 'edit':
      case 'deliver':
      case 'view': {
        const shipId = pkg?.shipment?.id
        if (shipId) {
          fetchShipmentDetails(shipId)
        } else if (activeMode === 'deliver') {
          setDeliveryDate('')
        }
        break
      }
      default:
        assertNever(activeMode)
    }
  }, [pkg, isDirect, shipmentData, explicitMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hydrate from pre-loaded shipment data (avoids extra API call) ──
  const hydrateFromShipmentData = (shipment: any) => {
    setFetchedShipmentId(shipment.id)
    setShipmentNumber(shipment.shipment_number ?? shipment.api_code ?? '')
    setShipmentMethod(shipment.shipping_method ?? '')
    setCustomShipmentMethod('')
    setShipDate(toInputDateTime(shipment.ship_date ?? shipment.api_created_at ?? ''))
    setDeliveryDate(shipment.delivery_date ? toInputDateTime(shipment.delivery_date) : '')
    setTrackingNumber(shipment.tracking_number ?? '')
    setTrackingUrl(shipment.tracking_url ?? '')

    // Build editable order items map for edit mode
    if (Array.isArray(shipment.order_items) || Array.isArray(shipment.line_items)) {
      const items = Array.isArray(shipment.order_items) ? shipment.order_items : shipment.line_items
      const itemMap: Record<number, number> = {}
      items.forEach((item: any) => {
        const itemId = item.platform_order_line_item ?? item.order_item_id ?? item.id
        if (itemId != null) {
          itemMap[itemId] = item.quantity ?? item.package_quantity ?? 0
        }
      })
      setEditableOrderItems(itemMap)
    }
  }

  // Hydrate direct create with 0 (or we could use pending_qty, but 0 aligns with package flow)
  const hydrateDirectCreate = () => {
    const itemMap: Record<number, number> = {}
    orderLinesList.forEach((li: any) => {
      if (li.id != null) {
        itemMap[li.id] = 0 // default to 0
      }
    })
    setEditableOrderItems(itemMap)
  }

  // ── Fetch shipment by ID (backward compat for package flow) ──
  const fetchShipmentDetails = async (shipmentId: number) => {
    try {
      const res: any = await dispatch(getOmsShipmentListAsync({ id: shipmentId })).unwrap()
      const shipment = Array.isArray(res?.data) ? res.data[0] : null
      if (shipment) {
        hydrateFromShipmentData(shipment)
      } else {
        // Fallback: use whatever is on pkg
        setFetchedShipmentId(Number(shipmentId))
        setDeliveryDate('')
      }
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to fetch shipment details.'))
    }
  }

  // ===================================================================
  // Submit Handlers — Isolated per mode
  // ===================================================================

  const buildFinalShippingMethod = (): string => {
    return shipmentMethod === 'others'
      ? (customShipmentMethod.trim() || 'Others')
      : shipmentMethod
  }

  const buildOrderItems = (): any[] => {
    // Edit mode, Deliver mode, or Direct Create mode: use editable order items map
    if (activeMode === 'edit' || activeMode === 'deliver' || (activeMode === 'create' && isDirect)) {
      return Object.entries(editableOrderItems)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({ id: Number(id), quantity: qty }))
    }
    // Package flow: from pkg.line_items
    return Array.isArray(pkg?.line_items)
      ? pkg.line_items
          .map((li: any) => ({
            id: li.platform_order_line_item ?? li.order_item_id ?? li.id ?? null,
            quantity: li.package_quantity ?? 0,
          }))
          .filter((item: any) => item.id !== null && item.quantity > 0)
      : []
  }

  /** Create mode: POST new shipment → then update status to SHIPPED */
  const handleSubmitCreate = async () => {
    if (!orderId) return
    if (!isDirect && !pkg?.id) return

    const orderItems = buildOrderItems()
    const finalShippingMethod = buildFinalShippingMethod()
    const order_package_id = pkg?.id ?? null

    const payload: any = {
      order_id: Number(orderId),
      shipment_number: shipmentNumber.trim() || null,
      order_items: orderItems,
      shipping_method: finalShippingMethod,
      ship_date: formatDateTimeForApi(shipDate),
      delivery_date: null,
      tracking_number: trackingNumber.trim(),
      tracking_url: trackingUrl.trim() || null,
      request_extra_data: [
        {
          type: "SHIPPINGMETHOD",
          value: finalShippingMethod
        }
      ]
    }

    // Flow 1 (package): include package_id
    // Flow 2 (direct): include marked_as_shipped
    if (isDirect) {
      payload.marked_as_shipped = true
    } else {
      payload.package_id = order_package_id
    }

    const res: any = await dispatch(createOmsShipmentFulfilledAsync(payload)).unwrap()
    if (res?.status && res.status >= 400) {
      errorToast(getApiErrorMsg(res, 'Failed to create shipment.'))
      return
    }

    const newShipmentId = res?.data?.shipment_id || res?.data?.id || res?.id
    if (!newShipmentId) {
      errorToast('Shipment created, but no ID returned for status update.')
      return
    }

    // Now update status to SHIPPED
    await dispatch(
      updateOmsShipmentStatusAsync({
        id: newShipmentId,
        shipment_status: 'SHIPPED',
        ship_date: formatDateTimeForApi(shipDate),
        order_id: Number(orderId),
        order_items: orderItems,
      })
    ).unwrap()

    successToast(res?.message || 'Shipment created and marked as shipped.')
  }

  /** Edit mode: PUT update shipment data → then update status to SHIPPED */
  const handleSubmitEdit = async () => {
    const activeShipmentId = fetchedShipmentId ?? shipmentData?.id ?? pkg?.shipment?.id
    if (!activeShipmentId) {
      errorToast('No shipment ID found. Cannot update shipment.')
      return
    }

    const orderItems = buildOrderItems()
    const finalShippingMethod = buildFinalShippingMethod()

    const payload = {
      order_id: Number(orderId),
      shipment_number: shipmentNumber.trim() || null,
      order_items: orderItems,
      shipping_method: finalShippingMethod,
      ship_date: formatDateTimeForApi(shipDate),
      delivery_date: null,
      tracking_number: trackingNumber.trim(),
      tracking_url: trackingUrl.trim() || null,
      request_extra_data: [
        {
          type: "SHIPPINGMETHOD",
          value: finalShippingMethod
        }
      ]
    }

    await dispatch(
      updateOmsShipmentFulfilledAsync({ id: activeShipmentId, data: payload })
    ).unwrap()

    await dispatch(
      updateOmsShipmentStatusAsync({
        id: activeShipmentId,
        shipment_status: 'SHIPPED',
        ship_date: formatDateTimeForApi(shipDate),
        order_id: Number(orderId),
        order_items: orderItems,
      })
    ).unwrap()

    successToast('Shipment updated successfully.')
  }

  /** Deliver mode: PUT update shipment data → then mark DELIVERED */
  const handleSubmitDeliver = async () => {
    const activeShipmentId = fetchedShipmentId ?? pkg?.shipment?.id
    if (!activeShipmentId) {
      errorToast('No shipment ID found. Cannot mark as delivered.')
      return
    }

    const orderItems = buildOrderItems()
    const finalShippingMethod = buildFinalShippingMethod()
    const order_package_id = pkg?.id ?? null

    const payload = {
      package_id: order_package_id,
      order_id: Number(orderId),
      shipment_number: shipmentNumber.trim() || null,
      order_items: orderItems,
      shipping_method: finalShippingMethod,
      ship_date: formatDateTimeForApi(shipDate),
      delivery_date: formatDateTimeForApi(deliveryDate),
      tracking_number: trackingNumber.trim(),
      tracking_url: trackingUrl.trim() || null,
      request_extra_data: [
        {
          type: "SHIPPINGMETHOD",
          value: finalShippingMethod
        }
      ]
    }

    await dispatch(
      updateOmsShipmentFulfilledAsync({ id: activeShipmentId, data: payload })
    ).unwrap()

    await dispatch(
      updateOmsShipmentStatusAsync({
        id: activeShipmentId,
        shipment_status: 'DELIVERED',
        delivery_date: formatDateTimeForApi(deliveryDate),
        order_id: Number(orderId),
        order_items: orderItems,
      })
    ).unwrap()

    successToast('Shipment marked as delivered successfully.')
  }

  /** Top-level submit dispatcher — routes to the correct isolated handler. */
  const handleSubmitShipment = async () => {
    try {
      switch (activeMode) {
        case 'create':
          await handleSubmitCreate()
          break
        case 'edit':
          await handleSubmitEdit()
          break
        case 'deliver':
          await handleSubmitDeliver()
          break
        case 'view':
          // View mode has no submit action
          return
        default:
          assertNever(activeMode)
      }
      onSuccess()
      onCancel()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Shipment operation failed.'))
    }
  }

  // ===================================================================
  // Computed / Derived values
  // ===================================================================

  const pkgId = pkg?.id
  const pkgName = pkg?.package_name || pkg?.name

  const effectiveShippingMethod = shipmentMethod === 'others' ? customShipmentMethod : shipmentMethod

  // Create mode: disabled if all fields empty
  const allFieldsEmptyCreate =
    !shipmentNumber.trim() &&
    !effectiveShippingMethod.trim() &&
    !shipDate.trim() &&
    !trackingNumber.trim() &&
    !trackingUrl.trim()

  // Deliver mode: disabled if no delivery date
  const deliverButtonDisabled = !deliveryDate.trim()

  // Edit mode or Direct Create mode: disable if no items have qty > 0
  const noEditableItemsSelected = Object.values(editableOrderItems).every(qty => qty === 0)
  const isEditableItemsMode = activeMode === 'edit' || (activeMode === 'create' && isDirect)
  const editButtonDisabled = isEditableItemsMode && noEditableItemsSelected

  /** Resolve submit-button disabled state per mode. */
  const isSubmitDisabled = (): boolean => {
    switch (activeMode) {
      case 'create': return allFieldsEmptyCreate || (isDirect && noEditableItemsSelected)
      case 'edit': return editButtonDisabled
      case 'deliver': return deliverButtonDisabled
      case 'view': return true
      default: assertNever(activeMode)
    }
  }

  const isSubmitting = shipmentSaving
  const isLoading = shipmentDetailLoading

  // ===================================================================
  // Editable order items handler (for edit mode)
  // ===================================================================

  const handleEditableItemQtyChange = (itemId: number, value: string, maxQty: number) => {
    const num = Math.max(0, Math.min(Number(value) || 0, maxQty))
    setEditableOrderItems((prev) => ({ ...prev, [itemId]: num }))
  }

  // ===================================================================
  // Determine which line items to show
  // ===================================================================

  const getDisplayLineItems = (): any[] => {
    if (activeMode === 'edit') {
      // For edit mode, show orderLinesList with editable quantities
      return orderLinesList
    }
    // For other modes, show pkg.line_items if available
    if (Array.isArray(pkg?.line_items) && pkg.line_items.length > 0) {
      return pkg.line_items
    }
    // For direct flow, show shipmentData items
    if (shipmentData) {
      if (Array.isArray(shipmentData.line_items) && shipmentData.line_items.length > 0) return shipmentData.line_items
      if (Array.isArray(shipmentData.order_items) && shipmentData.order_items.length > 0) return shipmentData.order_items
    }
    
    // Fallback: reconstruct from editableOrderItems
    return Object.entries(editableOrderItems)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ id: Number(id), quantity: qty }))
  }

  const displayLineItems = getDisplayLineItems()

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className='bg-white rounded-3' style={{ border: '1px solid #dee2e6' }}>
      <div
        style={{ borderBottom: '1px solid #dee2e6', padding: '16px 20px' }}
      >
        <div>
          <h5 style={{ fontSize: '17px', fontWeight: 700, margin: 0 }}>
            {config.title}
          </h5>
          <div className='d-flex align-items-center mt-2'>
            <Badge bg='light' text='secondary' className='border border-secondary px-3 py-2 rounded-pill d-flex align-items-center' style={{ fontSize: '12px', fontWeight: 500 }}>
              {pkgId ? `📦 Package ${packageIndex !== undefined ? packageIndex + 1 : `#${pkgId}`}` : `Order ${orderCode || `#${orderId}`}`}
              {pkgName ? ` — ${pkgName}` : ''}
              {fetchedShipmentId && (
                <span className='ms-2 badge bg-info' style={{ fontSize: '10px' }}>
                  Shipment {shipmentIndex !== undefined ? shipmentIndex + 1 : `#${fetchedShipmentId}`}
                </span>
              )}
            </Badge>
            {activeMode !== 'create' && (() => {
              const realStatus = (shipmentData?.shipment_status || shipmentData?.status || pkg?.shipment?.shipment_status || 'UNKNOWN').toUpperCase();
              let realBadgeBg = 'secondary';
              if (realStatus === 'SHIPPED') realBadgeBg = 'primary';
              if (realStatus === 'DELIVERED') realBadgeBg = 'success';
              if (realStatus === 'PENDING') realBadgeBg = 'warning text-dark';
              
              return (
                <Badge 
                  bg={realBadgeBg} 
                  className='ms-2'
                  style={{ fontSize: '10px' }}
                >
                  {realStatus}
                </Badge>
              );
            })()}
          </div>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        {isLoading ? (
          <div className='d-flex justify-content-center align-items-center py-5'>
            <Spinner animation='border' size='sm' className='me-2' />
            <span className='text-muted'>Loading shipment details...</span>
          </div>
        ) : (
          <>
            <style>{`
              .empty-native-input {
                color: #adb5bd !important;
                opacity: 0.6 !important;
              }
            `}</style>
            <Row className='mb-3'>
              <Col md={6} className='mb-3 mb-md-0'>
                <label className='form-label fw-bold mb-1' style={{ fontSize: '13px', color: '#333' }}>
                  Shipment Number
                  <span className='text-muted fw-normal ms-1' style={{ fontSize: '12px' }}>(Optional)</span>
                </label>
                <Form.Control
                  type='text'
                  placeholder={config.fieldsDisabled ? '' : 'e.g. SHP-2026-001'}
                  value={shipmentNumber}
                  onChange={(e) => setShipmentNumber(e.target.value)}
                  style={{ fontSize: '14px' }}
                  disabled={isSubmitting || config.fieldsDisabled}
                />
              </Col>
              <Col md={6}>
                <label className='form-label fw-bold mb-1' style={{ fontSize: '13px', color: '#333' }}>
                  Shipping Method
                </label>
                {config.fieldsDisabled ? (
                  <Form.Control
                    type='text'
                    value={shipmentMethod || '—'}
                    style={{ fontSize: '14px' }}
                    disabled
                  />
                ) : (
                  <ShippingCarrierSelect
                    value={shipmentMethod}
                    customValue={customShipmentMethod}
                    onChange={(apiId, customText) => {
                      setShipmentMethod(apiId)
                      setCustomShipmentMethod(customText)
                    }}
                    disabled={config.fieldsDisabled}
                    isSubmitting={isSubmitting}
                  />
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col md={6} className='mb-3 mb-md-0'>
                <label className='form-label fw-bold mb-1' style={{ fontSize: '13px', color: '#333' }}>
                  Ship Date
                </label>
                <Form.Control
                  type='datetime-local'
                  value={shipDate}
                  onChange={(e) => setShipDate(e.target.value)}
                  style={{ fontSize: '14px' }}
                  className={!shipDate ? 'empty-native-input' : ''}
                  disabled={isSubmitting || config.fieldsDisabled}
                />
              </Col>
              {/* Delivery Date: visibility controlled by config */}
              {config.deliveryDateVisible && (
                <Col md={6}>
                  <label 
                    className='form-label fw-bold mb-1' 
                    style={{ 
                      fontSize: '13px', 
                      color: activeMode === 'deliver' ? '#198754' : '#333' 
                    }}
                  >
                    Delivery Date {activeMode === 'deliver' && <span style={{fontSize: '11px', fontWeight: 'normal', color: '#198754'}}>(Required)</span>}
                  </label>
                  <Form.Control
                    type='datetime-local'
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    style={{ 
                      fontSize: '14px',
                      ...(activeMode === 'deliver' ? { 
                        borderColor: '#198754', 
                        backgroundColor: '#f8fff9', 
                        borderWidth: '2px', 
                        boxShadow: '0 0 0 0.2rem rgba(25, 135, 84, 0.15)' 
                      } : {})
                    }}
                    className={!deliveryDate ? 'empty-native-input' : ''}
                    disabled={isSubmitting || config.deliveryDateDisabled}
                  />
                </Col>
              )}
            </Row>

            <Row className='mb-3'>
              <Col md={6} className='mb-3 mb-md-0'>
                <label className='form-label fw-bold mb-1' style={{ fontSize: '13px', color: '#333' }}>
                  Tracking Number
                </label>
                <Form.Control
                  type='text'
                  placeholder={config.fieldsDisabled ? '' : 'e.g. 1Z999AA10123456784'}
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  style={{ fontSize: '14px' }}
                  disabled={isSubmitting || config.fieldsDisabled}
                />
              </Col>
              <Col md={6}>
                <label className='form-label fw-bold mb-1' style={{ fontSize: '13px', color: '#333' }}>
                  Tracking URL
                  <span className='text-muted fw-normal ms-1' style={{ fontSize: '12px' }}>(Optional)</span>
                </label>
                <Form.Control
                  type='url'
                  placeholder={config.fieldsDisabled ? '' : 'e.g. - https://tracking.com/...'}
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  style={{ fontSize: '14px' }}
                  disabled={isSubmitting || config.fieldsDisabled}
                />
              </Col>
            </Row>

            {/* ── Order Line Items Section ── */}
            {/* Editable quantity inputs for Edit mode or Direct Create mode */}
            {isEditableItemsMode && orderLinesList.length > 0 && (
              <div className='mt-3'>
                <h6 className='fw-bold mb-2' style={{ fontSize: '14px', color: '#333' }}>
                  {activeMode === 'edit' ? 'Edit Items in Shipment' : 'Items to Ship'}
                </h6>
                <table className='table table-sm align-middle mb-0' style={{ fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ fontWeight: 600, color: '#666', padding: '8px 12px' }}>Product</th>
                      <th style={{ fontWeight: 600, color: '#666', padding: '8px 12px' }}>SKU</th>
                      <th style={{ fontWeight: 600, color: '#666', padding: '8px 12px' }} className='text-center'>Quantity</th>
                      <th style={{ fontWeight: 600, color: '#666', padding: '8px 12px' }} className='text-center'>Shipped</th>
                      <th style={{ fontWeight: 600, color: '#666', padding: '8px 12px' }} className='text-center'>Pending Qty</th>
                      <th style={{ fontWeight: 600, color: '#666', padding: '8px 12px', minWidth: '100px' }} className='text-center'>Qty to Ship</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderLinesList.map((item: any, idx: number) => {
                      const itemId = item.id
                      const itemName = item.name ?? item.title ?? item.platform_product?.name ?? `Item #${itemId}`
                      const sku = item.sku ?? item.platform_product_variant?.sku ?? 'N/A'
                      
                      // Calculate maximum allowed quantity for this input
                      // In Create mode: it's just pending_qty
                      // In Edit mode: it's pending_qty + whatever was already in this shipment
                      let alreadyInThisShipment = 0
                      if (activeMode === 'edit' && shipmentData) {
                        const items = Array.isArray(shipmentData.order_items) ? shipmentData.order_items : shipmentData.line_items || []
                        const matching = items.find((si: any) => (si.platform_order_line_item ?? si.order_item_id ?? si.id) === itemId)
                        if (matching) {
                          alreadyInThisShipment = matching.quantity ?? matching.package_quantity ?? 0
                        }
                      }
                      
                      const calculatedMax = (item.pending_qty ?? 0) + alreadyInThisShipment
                      const maxAllowed = Math.min(calculatedMax, item.quantity ?? calculatedMax)
                      const shipped = (item.quantity ?? 0) - (item.pending_qty ?? 0)
                      const currentQty = editableOrderItems[itemId] ?? 0

                      return (
                        <tr key={`${itemId}-${idx}`}>
                          <td style={{ padding: '8px 12px' }}>{itemName}</td>
                          <td style={{ padding: '8px 12px' }}>{sku}</td>
                          <td className='text-center' style={{ padding: '8px 12px' }}>{item.quantity ?? 0}</td>
                          <td className='text-center' style={{ padding: '8px 12px' }}>{shipped}</td>
                          <td className='text-center' style={{ padding: '8px 12px' }}>
                            <span className={item.pending_qty > 0 ? 'text-success fw-semibold' : 'text-muted'}>
                              {item.pending_qty ?? 0}
                            </span>
                          </td>
                          <td className='text-center' style={{ padding: '8px 12px' }}>
                            <Form.Control
                              type='number'
                              min='0'
                              max={maxAllowed}
                              value={currentQty}
                              style={{
                                maxWidth: '70px',
                                margin: '0 auto',
                                fontSize: '13px',
                                border: '1px solid #dee2e6',
                                textAlign: 'center',
                              }}
                              onChange={(e) => handleEditableItemQtyChange(itemId, e.target.value, maxAllowed)}
                              disabled={isSubmitting || maxAllowed === 0}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Non-editable modes: read-only line items display */}
            {!isEditableItemsMode && displayLineItems.length > 0 && (
              <div
                className='rounded p-3 mt-2'
                style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
              >
                <p className='fw-bold mb-2' style={{ fontSize: '13px', color: '#555' }}>
                  Items in this shipment:
                </p>
                <div className='table-responsive'>
                  <table className='table table-sm table-borderless mb-0'>
                    <thead className='text-muted' style={{ fontSize: '12px' }}>
                      <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                        <th className='fw-semibold pb-2 ps-0' style={{ width: '15%' }}>Qty</th>
                        <th className='fw-semibold pb-2' style={{ width: '50%' }}>Product Name</th>
                        <th className='fw-semibold pb-2'>SKU</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayLineItems.map((li: any, idx: number) => {
                        const lineItemId = li.platform_order_line_item ?? li.order_item_id ?? li.id
                        const matchedLine = orderLinesList.find((ol: any) => ol.id === lineItemId)
                        const itemName =
                          li.name ??
                          matchedLine?.name ??
                          matchedLine?.title ??
                          matchedLine?.platform_product?.name ??
                          `Item #${lineItemId}`
                        const sku =
                          li.sku ??
                          matchedLine?.sku ??
                          matchedLine?.platform_product_variant?.sku ??
                          'N/A'
                        const qty = li.package_quantity ?? li.quantity ?? 0

                        return (
                          <tr key={`${li.id ?? lineItemId}-${idx}`}>
                            <td className='align-middle ps-0 pt-2'>
                              <span style={{ backgroundColor: '#0d6efd', color: 'white', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: 'bold' }}>
                                {qty}
                              </span>
                            </td>
                            <td className='align-middle fw-semibold pt-2' style={{ fontSize: '13px', color: '#333' }}>
                              {itemName}
                            </td>
                            <td className='align-middle pt-2'>
                              {sku !== 'N/A' ? (
                                <span style={{ backgroundColor: '#fff', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  {sku}
                                </span>
                              ) : (
                                <span className='text-muted' style={{ fontSize: '12px' }}>N/A</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className='d-flex justify-content-end gap-2' style={{ borderTop: '1px solid #dee2e6', padding: '16px 20px', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
        <Button
          variant='light'
          onClick={onCancel}
          disabled={isSubmitting}
          style={{ padding: '8px 24px', fontWeight: 500, color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }}
        >
          {config.closeButtonText}
        </Button>
        {config.showSubmitButton && (
          <Button
            variant={config.submitButtonVariant}
            onClick={handleSubmitShipment}
            disabled={isSubmitting || isLoading || isSubmitDisabled()}
            style={{ fontSize: '14px', minWidth: '160px' }}
          >
            {isSubmitting ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                {config.submittingText}
              </>
            ) : (
              config.submitButtonText
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

export default ShipmentFulfillmentForm
